import { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Copy, Trash2, ArrowLeft, ArrowRight, ZoomIn, ZoomOut, Maximize, RotateCcw, Wrench } from 'lucide-react';
import { HerrajeCatalogo } from './HerrajeCatalogo';
import { HerrajeSimbolo, HerrajePanel, sugeridoUnion, validarRegla } from './ModuladorHerrajes';
import { AyudaTip } from './MenuAyudaSA';
import { VistaTecnica } from './VistaTecnica';
import { derivarModulos, detectarUniones } from './simbolos/modeloTecnico';

// MODELO ÚNICO: los módulos se derivan de la receta (modelo técnico compartido con la
// Vista Técnica y el Anexo PDF — ver ./simbolos/modeloTecnico.js).
export { derivarModulos };

export const derivarDiseno = (receta, familia) => {
  const mods = derivarModulos(receta, familia);
  const alto = Math.max(...mods.map((m) => m.alto_mm));
  return {
    tipo: 'ventana2d', fuente: 'modulador',
    ancho_mm: mods.reduce((a, m) => a + m.ancho_mm, 0), alto_mm: alto,
    cols_mm: mods.map((m) => m.ancho_mm), rows_mm: [alto],
    modulos: mods.map((m) => ({ tipo_hoja: m.tipo === 'PABM' ? 'abatible' : 'fijo',
      apertura: m.tipo === 'PABM' ? (m.apertura || 'derecha') : '', rol: m.id, ancho_mm: m.ancho_mm, alto_mm: m.alto_mm })),
  };
};

export const Modulador = ({ familia, receta, onChange }) => {
  const r = receta || {};
  const [sel, setSel] = useState(null);        // id de módulo, `union_{i}` o `herraje_{hid}`
  const [zoom, setZoom] = useState(1);
  const [vista, setVista] = useState('mod');   // 'mod' = modulación (trabajo) | 'tec' = vista técnica (presentación)
  const [picker, setPicker] = useState(null);  // { open, ancla, sugerencia }
  const [drag, setDrag] = useState(null);      // { hid, dx, dy }
  const [dragPos, setDragPos] = useState(null);
  const [vivo, setVivo] = useState(null);       // item en modo INSERTAR VIVO (clics múltiples)
  const [acabados, setAcabados] = useState([]);
  const svgRef = useRef(null);
  useEffect(() => {
    if (!r.vano_alto) onChange({ ...r, familia, vano_alto: 2000 });  // alto por defecto REAL en la receta (sin fantasmas)
    api.get(`/sa/recetas/${familia}`).then((x) => setAcabados(x.data?.acabados || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const mods = useMemo(() => derivarModulos(r, familia), [r, familia]);
  // limpieza de anclas colgantes: módulos renombrados/eliminados → libre · símbolo de unión inexistente → se quita
  useEffect(() => {
    const ids = new Set([...mods.map((m) => m.id), 'muro']);
    const us = r.uniones || [];
    const usLimpias = us.filter((u) => ids.has(u.componente_a) && ids.has(u.componente_b));
    const uids = new Set(usLimpias.map((u) => `${u.componente_a}↔${u.componente_b}`));
    const hs = r.herrajes_visuales || [];
    const limpio = hs.filter((h) => !(h.solo_visual && h.union_id && !uids.has(h.union_id)))
      .map((h) => h.modulo_id && !ids.has(h.modulo_id) ? { ...h, modulo_id: '', pos_rel: 'libre' } : h);
    const unionesCambiaron = usLimpias.length !== us.length;
    if (unionesCambiaron || limpio.length !== hs.length || limpio.some((h, i) => h !== hs[i])) {
      onChange({ ...r, familia, herrajes_visuales: limpio,
        ...(unionesCambiaron ? { uniones: usLimpias, despiece_estado: 'pendiente' } : {}) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mods]);
  const totalW = mods.reduce((a, m) => a + m.ancho_mm, 0);
  const totalH = Math.max(...mods.map((m) => m.alto_mm));
  const set = (k, v) => onChange({ ...r, familia, [k]: v, despiece_estado: 'pendiente' });
  // cambios de GEOMETRÍA → detección automática de uniones (la geometría propone, el usuario confirma/edita; nunca modifica uniones existentes)
  const setGeo = (patch) => {
    const nr = { ...r, familia, ...patch, despiece_estado: 'pendiente' };
    const nuevas = detectarUniones(nr, familia);
    if (nuevas.length) {
      nr.uniones = [...(nr.uniones || []), ...nuevas];
      toast.info(`${nuevas.length} unión(es) detectada(s) por geometría — toca el rombo U para revisar ángulo, cantidad y herraje`, { duration: 6000 });
    }
    onChange(nr);
  };
  const detectar = () => {
    const nuevas = detectarUniones(r, familia);
    if (!nuevas.length) return toast.info('Geometría ya representada — no hay uniones nuevas que proponer');
    onChange({ ...r, familia, uniones: [...(r.uniones || []), ...nuevas], despiece_estado: 'pendiente' });
    toast.info(`${nuevas.length} unión(es) detectada(s) — revisa ángulo, cantidad y herraje en cada rombo U`, { duration: 6000 });
  };
  const setFijo = (idx, patch) => { const a = [...(r.fijos || [])]; a[idx] = { ...a[idx], ...patch }; patch.posicion ? setGeo({ fijos: a }) : set('fijos', a); };
  const addVTF = (pos) => setGeo({ fijos: [...(r.fijos || []), { posicion: pos, vano_ancho: 500, vano_alto: r.vano_alto || 2000, barra: false }] });
  const modSel = mods.find((m) => m.id === sel);
  const unionesDe = (id) => (r.uniones || []).map((u, i) => ({ ...u, _i: i }))
    .filter((u) => u.componente_a === id || u.componente_b === id);
  const setUnion = (i, patch) => { const a = [...(r.uniones || [])]; const nu = { ...a[i], ...patch };
    if (patch.angulo && nu.tipo_herraje && nu.tipo_herraje !== 'otro' && !(nu.componente_a === 'muro' || nu.componente_b === 'muro')) {
      const pf = nu.componente_a === 'puerta' || nu.componente_b === 'puerta';
      nu.tipo_herraje = `${pf ? 'bisagra' : 'herraje'} vidrio-vidrio ${patch.angulo}°`;
    }
    a[i] = nu; set('uniones', a); };

  // geometría SVG proporcional (1 px = 1 mm, escalado por viewBox — nunca se deforma)
  const PAD = 90;
  const vw = totalW + PAD * 2, vh = totalH + PAD * 2;
  const cotY = totalH + PAD + 34;
  const layout = useMemo(() => {
    let x = PAD;
    return mods.map((m) => { const o = { ...m, x, y: PAD + (totalH - m.alto_mm) }; x += m.ancho_mm; return o; });
  }, [mods, totalH]);

  // ── HERRAJES VISUALES (Etapa 2) ──
  const herrajes = r.herrajes_visuales || [];
  const unis = (r.uniones || []).map((u, i) => ({ ...u, _i: i }));
  const unionPoint = (u) => {
    if (u.componente_a === 'muro' || u.componente_b === 'muro') {
      const M = layout.find((m) => m.id === (u.componente_a === 'muro' ? u.componente_b : u.componente_a));
      if (M) return { x: (u.lado_muro === 'der') ? M.x + M.ancho_mm : M.x, y: PAD + totalH * 0.25 };
    }
    const A = layout.find((m) => m.id === u.componente_a), B = layout.find((m) => m.id === u.componente_b);
    if (A && B) {
      const [L, R] = A.x <= B.x ? [A, B] : [B, A];
      return { x: (L.x + L.ancho_mm + R.x) / 2, y: PAD + totalH / 2 };
    }
    return { x: PAD + totalW / 2, y: PAD - 40 };
  };
  const unionSelI = sel && sel.startsWith('union_') ? Number(sel.slice(6)) : null;
  const unionSel = unionSelI !== null ? unis[unionSelI] : null;
  const herrSel = sel && sel.startsWith('herraje_') ? herrajes.find((h) => `herraje_${h.hid}` === sel) : null;
  const unionIdxDe = (h) => (r.uniones || []).findIndex((u) => `${u.componente_a}↔${u.componente_b}` === h.union_id);

  const setHerrajes = (arr) => set('herrajes_visuales', arr);                                 // cambios técnicos → RECÁLCULO PENDIENTE
  const patchHerraje = (hid, patch) => setHerrajes(herrajes.map((h) => h.hid === hid ? { ...h, ...patch } : h));
  const moverHerraje = (hid, x, y) => onChange({ ...r, familia,                               // mover NO cambia SKU/cantidad/costo → sin recálculo
    herrajes_visuales: herrajes.map((h) => h.hid === hid ? { ...h, x: Math.round(x), y: Math.round(y) } : h) });
  const eliminarHerraje = (h) => {
    setHerrajes(herrajes.filter((x) => x.hid !== h.hid)); setSel(null);
    toast.warning('⚠️ RECÁLCULO PENDIENTE — herraje eliminado. Usa "Calcular receta" para actualizar el despiece.', { duration: 6000 });
  };

  const insertarCon = (item, ancla, opts = {}) => {
    let x = PAD + totalW / 2, y = PAD + totalH / 2, modulo_id = '', union_id = '', cantidad = 1;
    if (ancla.tipo === 'modulo' && ancla.modulo) {
      const L = layout.find((m) => m.id === ancla.modulo.id) || layout[0];
      x = L.x + L.ancho_mm / 2; y = L.y + L.alto_mm / 2; modulo_id = L.id;
    }
    if (ancla.tipo === 'union' && ancla.union) {
      const u = ancla.union; const p = unionPoint(u);
      x = p.x; y = Math.max(PAD + 140, p.y - 300);  // offset: no tapar el rombo "U" de la unión
      union_id = `${u.componente_a}↔${u.componente_b}`;
      const pf = u.componente_a === 'puerta' || u.componente_b === 'puerta';
      cantidad = Number(u.cantidad || u.cantidad_sugerida || (pf ? 2 : 1));
    }
    const h = { hid: `h_${Date.now().toString(36)}${Math.floor(Math.random() * 999)}`,
      sku: item.codigo || '', nombre: item.nombre, unidad: item.unidad || 'unidad',
      cantidad: opts.soloVisual ? 0 : cantidad, acabado: item.acabado || r.acabado || '',
      costo: Number(item.costo || 0), observaciones: '', modulo_id, union_id,
      x: Math.round(x), y: Math.round(y), pos_rel: ancla.tipo, orientacion: 'vertical',
      etiqueta: `H${herrajes.length + 1}`,
      solo_visual: !!opts.soloVisual };
    const aviso = validarRegla(item.nombre, ancla, r);
    if (aviso) toast.warning(aviso, { duration: 7000 });
    if (!opts.soloVisual && !Number(item.costo || 0)) {
      toast.warning(`⚠️ COSTO PENDIENTE: "${item.nombre}" sin precio — al Calcular receta podrás asignarlo (solo este presupuesto o Catálogo Soluarq)`, { duration: 7000 });
    }
    setHerrajes([...herrajes, h]); setPicker(null); setSel(`herraje_${h.hid}`);
    if (!opts.soloVisual && ancla.tipo === 'libre') {
      setVivo(item);
      toast.info(`Modo VIVO: clic en el dibujo para insertar más "${item.nombre}" — ✕ para terminar`, { duration: 5000 });
    }
  };

  const insertarVivoEn = (x, y) => {
    if (!vivo) return;
    const h = { hid: `h_${Date.now().toString(36)}${Math.floor(Math.random() * 999)}`,
      sku: vivo.codigo || '', nombre: vivo.nombre, unidad: vivo.unidad || 'unidad',
      cantidad: 1, acabado: vivo.acabado || r.acabado || '',
      costo: Number(vivo.costo || 0), observaciones: '', modulo_id: '', union_id: '',
      x: Math.round(x), y: Math.round(y), pos_rel: 'libre', orientacion: 'vertical',
      etiqueta: `H${herrajes.length + 1}`, solo_visual: false };
    setHerrajes([...herrajes, h]);
  };

  const abrirPicker = (anclaForzada = null) => {
    let ancla = anclaForzada || { tipo: 'libre' };
    if (!anclaForzada) {
      if (unionSel) ancla = { tipo: 'union', i: unionSelI, union: unionSel };
      else if (modSel) ancla = { tipo: 'modulo', modulo: modSel };
    }
    setPicker({ open: true, ancla, sugerencia: ancla.tipo === 'union' ? sugeridoUnion(ancla.union) : '' });
  };

  const usarSugerido = async (u) => {
    const nombre = sugeridoUnion(u);
    let item = { nombre, codigo: '', costo: 0, unidad: 'unidad' };
    try {
      const { data } = await api.get('/sa/catalogo', { params: { q: nombre.replace('°', '') } });
      if (data.items?.length) item = data.items[0];
    } catch { /* sin catálogo → símbolo igual, la unión aporta la línea */ }
    insertarCon(item, { tipo: 'union', i: u._i, union: u }, { soloVisual: true });
    toast.success(`Símbolo "${item.nombre}" vinculado a la unión — la cantidad/costo la aporta la unión Fase 4 (sin duplicar)`, { duration: 6000 });
  };

  // drag con pointer events (mouse + touch)
  const toSvg = (e) => {
    const pt = svgRef.current.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
  };
  const onMove = (e) => { if (!drag) return; e.preventDefault(); const p = toSvg(e); setDragPos({ x: p.x - drag.dx, y: p.y - drag.dy }); };
  const onUp = () => { if (drag) { if (dragPos) moverHerraje(drag.hid, dragPos.x, dragPos.y); setDrag(null); setDragPos(null); } };

  const anclaLabel = (picker?.ancla?.tipo === 'union' || picker?.ancla?.tipo === 'union-item') && picker.ancla.union
    ? `unión ${picker.ancla.union.componente_a}↔${picker.ancla.union.componente_b}`
    : picker?.ancla?.tipo === 'modulo' ? `módulo ${picker.ancla.modulo?.id}` : 'punto libre';

  return (
    <div className="border border-[#0EA5E9]/40 rounded-lg p-2 space-y-2 bg-[var(--card)] min-w-0 max-w-full" data-testid="modulador">
      <div className="flex items-center justify-between flex-wrap gap-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-[10px] font-black uppercase text-[#0EA5E9]">Modulador visual Soluarq</p>
          <div className="flex rounded-md border border-[var(--border)] overflow-hidden">
            <button type="button" onClick={() => setVista('mod')} data-testid="mod-vista-modulacion"
              className={`px-2 py-1 text-[10px] font-bold uppercase ${vista === 'mod' ? 'bg-[#0EA5E9] text-white' : 'text-[var(--text-muted)]'}`}>Vista modulación</button>
            <button type="button" onClick={() => setVista('tec')} data-testid="mod-vista-tecnica"
              className={`px-2 py-1 text-[10px] font-bold uppercase ${vista === 'tec' ? 'bg-[#0F172A] text-white' : 'text-[var(--text-muted)]'}`}>Vista técnica</button>
          </div>
        </div>
        {vista === 'mod' && (
        <div className="flex gap-1 flex-wrap">
          {familia === 'pabm' && <>
            <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]" onClick={() => addVTF('izquierda')} data-testid="mod-add-vtf-izq"><Plus size={12} /> VTF izq</Button>
            <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]" onClick={() => addVTF('derecha')} data-testid="mod-add-vtf-der"><Plus size={12} /> VTF der</Button>
          </>}
          <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] border-amber-500/60 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600" onClick={detectar} data-testid="mod-detectar-uniones">
            Detectar uniones
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] border-amber-500/60 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600" onClick={() => { setVivo(null); abrirPicker(); }} data-testid="mod-insertar-herraje">
            <Wrench size={12} /> + Insertar herraje{herrajes.length ? ` (${herrajes.length})` : ''}
          </Button>
          {vivo && (
            <span className="text-[10px] font-black text-emerald-600 flex items-center gap-1 border border-emerald-500/50 rounded-full px-2 py-0.5" data-testid="mod-vivo">
              INSERTANDO: {vivo.nombre.slice(0, 24)} — clic en el dibujo
              <button type="button" className="ml-1 font-black text-red-500" onClick={() => setVivo(null)} data-testid="mod-vivo-off">✕</button>
            </span>
          )}
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]" onClick={() => setZoom(Math.min(3, zoom + 0.25))} data-testid="mod-zoom-in"><ZoomIn size={13} /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]" onClick={() => setZoom(Math.max(0.4, zoom - 0.25))} data-testid="mod-zoom-out"><ZoomOut size={13} /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]" onClick={() => setZoom(1)} title="Ajustar a pantalla" data-testid="mod-fit"><Maximize size={13} /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]" onClick={() => { setZoom(1); setSel(null); }} title="Restablecer" data-testid="mod-reset"><RotateCcw size={13} /></Button>
        </div>
        )}
      </div>
      {vista === 'tec' && <VistaTecnica familia={familia} receta={r} />}
      {vista === 'mod' && (<>
      <div className="overflow-auto border border-[var(--border)] rounded-md bg-white" style={{ maxHeight: 380 }}>
        <svg ref={svgRef} viewBox={`0 0 ${vw} ${vh}`} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
          onClick={(e) => { if (vivo) { const pnt = toSvg(e); insertarVivoEn(pnt.x, pnt.y); } else setSel(null); }} className="max-md:max-w-full"
          style={{ height: 340 * zoom, width: 'auto', display: 'block', margin: '0 auto', touchAction: 'none' }} data-testid="mod-canvas">
          {/* muro (referencia visual cuando la puerta va embisagrada a muro) */}
          {familia === 'pabm' && (r.embisagrado || 'muro') === 'muro' && (
            <rect x={PAD - 32} y={PAD} width={28} height={totalH} fill="#CBD5E1" stroke="#334155" strokeWidth={4} />
          )}
          {layout.map((m) => {
            const { x, y } = m;
            const esSel = sel === m.id;
            const esPuerta = m.tipo === 'PABM';
            const bisIzq = esPuerta && ((m.embisagrado === 'muro' && m.apertura !== 'izquierda') || (m.embisagrado !== 'muro' && m.apertura !== 'izquierda'));
            const nBis = m.alto_mm > 1800 ? 3 : 2;
            const bx = bisIzq ? x + 18 : x + m.ancho_mm - 18;
            const jx = bisIzq ? x + m.ancho_mm - 30 : x + 30;
            return (
              <g key={m.id} onClick={(e) => { e.stopPropagation(); setSel(m.id); }} style={{ cursor: 'pointer' }} data-testid={`mod-modulo-${m.id}`}>
                <rect x={x} y={y} width={m.ancho_mm} height={m.alto_mm} fill={esPuerta ? '#E0F2FE' : '#F1F5F9'}
                  stroke={esSel ? '#0EA5E9' : '#334155'} strokeWidth={esSel ? 14 : 6} />
                {esPuerta ? (<>
                  {/* apertura: diagonales técnicas desde el lado embisagrado */}
                  <path d={`M ${bx} ${y} L ${bisIzq ? x + m.ancho_mm : x} ${y + m.alto_mm / 2} L ${bx} ${y + m.alto_mm}`}
                    fill="none" stroke="#0284C7" strokeWidth={5} strokeDasharray="26 18" />
                  {Array.from({ length: nBis }).map((_, i) => (
                    <circle key={i} cx={bx} cy={y + ((i + 1) * m.alto_mm) / (nBis + 1)} r={22} fill="#0F172A" />
                  ))}
                  <rect x={jx - 8} y={y + m.alto_mm / 2 - 90} width={16} height={180} rx={8} fill="#0F172A" />
                </>) : (<>
                  <line x1={x + 30} y1={y + 30} x2={x + m.ancho_mm - 30} y2={y + m.alto_mm - 30} stroke="#94A3B8" strokeWidth={4} />
                  <line x1={x + m.ancho_mm - 30} y1={y + 30} x2={x + 30} y2={y + m.alto_mm - 30} stroke="#94A3B8" strokeWidth={4} />
                  <text x={x + m.ancho_mm / 2} y={y + 70} textAnchor="middle" fontSize={52} fill="#64748B" fontWeight="700">FIJO</text>
                </>)}
                {/* cota del módulo */}
                <text x={x + m.ancho_mm / 2} y={cotY} textAnchor="middle" fontSize={54} fill="#0F172A" fontWeight="700">{m.ancho_mm}</text>
                <line x1={x} y1={cotY - 44} x2={x + m.ancho_mm} y2={cotY - 44} stroke="#0F172A" strokeWidth={3} />
              </g>
            );
          })}
          {/* marcadores de UNIÓN (Fase 4 = fuente técnica) — clic para seleccionar */}
          {unis.map((u) => {
            const p = unionPoint(u);
            const activa = sel === `union_${u._i}`;
            return (
              <g key={`u${u._i}`} onClick={(e) => { e.stopPropagation(); setSel(`union_${u._i}`); }} style={{ cursor: 'pointer' }} data-testid={`mod-union-marker-${u._i}`}>
                <rect x={p.x - 28} y={p.y - 28} width={56} height={56} transform={`rotate(45 ${p.x} ${p.y})`}
                  fill={activa ? '#F59E0B' : '#FDE68A'} stroke="#B45309" strokeWidth={7} />
                <text x={p.x} y={p.y + 12} textAnchor="middle" fontSize={30} fill="#78350F" fontWeight="800">{`U${u._i + 1}`}</text>
              </g>
            );
          })}
          {/* herrajes visuales — arrastrables, vinculados a herrajes_visuales */}
          {herrajes.map((h) => {
            const pos = drag?.hid === h.hid && dragPos ? dragPos : { x: h.x, y: h.y };
            return (
              <HerrajeSimbolo key={h.hid} h={h} x={pos.x} y={pos.y} sel={sel === `herraje_${h.hid}`}
                onClick={(e) => { e.stopPropagation(); setSel(`herraje_${h.hid}`); }}
                onPointerDown={(e) => {
                  e.stopPropagation(); setSel(`herraje_${h.hid}`);
                  const p = toSvg(e); setDrag({ hid: h.hid, dx: p.x - h.x, dy: p.y - h.y });
                }} />
            );
          })}
          {/* cota total + alto */}
          <text x={PAD + totalW / 2} y={cotY + 56} textAnchor="middle" fontSize={58} fill="#0284C7" fontWeight="800" data-testid="mod-cota-total">TOTAL {totalW} mm</text>
          <text x={PAD - 34} y={PAD + totalH / 2} textAnchor="middle" fontSize={54} fill="#0F172A" fontWeight="700"
            transform={`rotate(-90 ${PAD - 34} ${PAD + totalH / 2})`}>{totalH} mm</text>
        </svg>
      </div>
      {/* panel de propiedades del módulo seleccionado */}
      {modSel && (
        <div className="border border-[var(--border)] rounded-md p-2 space-y-1.5" data-testid="mod-panel">
          <p className="text-sm font-black text-[#0EA5E9]">⚙️ Configurar {modSel.tipo === 'puerta' ? 'puerta' : 'módulo'} <span className="text-[10px] text-[var(--text-muted)] uppercase">({modSel.id})</span></p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 items-end">
            <div><label className="text-[10px] font-bold text-[var(--text-muted)]">Ancho (mm)</label>
              <Input type="number" className="h-9 text-xs" value={modSel.ancho_mm} data-testid="mod-ancho"
                onChange={(e) => { const v = e.target.value; modSel.id === 'puerta' || modSel.id === 'fijo' ? set('vano_ancho', v) : setFijo(modSel.idx, { vano_ancho: v }); }} /></div>
            <div><label className="text-[10px] font-bold text-[var(--text-muted)]">Alto (mm)</label>
              <Input type="number" className="h-9 text-xs" value={modSel.alto_mm} data-testid="mod-alto"
                onChange={(e) => { const v = e.target.value; modSel.id === 'puerta' || modSel.id === 'fijo' ? set('vano_alto', v) : setFijo(modSel.idx, { vano_alto: v }); }} /></div>
            {modSel.tipo === 'PABM' && (
              <div><label className="text-[10px] font-bold text-[var(--text-muted)]">Apertura</label>
                <div className="flex rounded-md border border-[var(--border)] overflow-hidden h-9" data-testid="mod-apertura">
                  {[['izquierda', '← Izquierda'], ['derecha', 'Derecha →']].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => set('apertura', v)} data-testid={`mod-apertura-${v}`}
                      className={`flex-1 text-[11px] font-bold ${(r.apertura || 'derecha') === v ? 'bg-[#0EA5E9] text-white' : 'text-[var(--text-muted)] bg-[var(--card)]'}`}>{l}</button>
                  ))}
                </div></div>
            )}
            {modSel.tipo === 'VTF' && modSel.idx !== undefined && (
              <div className="flex gap-1">
                <Button type="button" variant="ghost" size="sm" className="h-9 px-2" title="Mover a la izquierda de la puerta"
                  onClick={() => setFijo(modSel.idx, { posicion: 'izquierda' })} data-testid="mod-mover-izq"><ArrowLeft size={13} /></Button>
                <Button type="button" variant="ghost" size="sm" className="h-9 px-2" title="Mover a la derecha de la puerta"
                  onClick={() => setFijo(modSel.idx, { posicion: 'derecha' })} data-testid="mod-mover-der"><ArrowRight size={13} /></Button>
                <Button type="button" variant="ghost" size="sm" className="h-9 px-2" title="Duplicar"
                  onClick={() => setGeo({ fijos: [...(r.fijos || []), { ...(r.fijos || [])[modSel.idx] }] })} data-testid="mod-duplicar"><Copy size={13} /></Button>
                <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-red-400" title="Eliminar"
                  onClick={() => { setGeo({ fijos: (r.fijos || []).filter((_, j) => j !== modSel.idx) }); setSel(null); }} data-testid="mod-eliminar"><Trash2 size={13} /></Button>
              </div>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] border-amber-500/60 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600"
            onClick={() => abrirPicker({ tipo: 'modulo', modulo: modSel })} data-testid="mod-herraje-en-modulo">
            <Wrench size={12} /> + Insertar herraje en este módulo
          </Button>
          {/* uniones del módulo — misma entidad técnica de Fase 4 (ángulo + holgura editables) */}
          {unionesDe(modSel.id).map((u) => {
            const esMuroU = u.componente_a === 'muro' || u.componente_b === 'muro';
            return (
            <div key={u._i} className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 items-end border-t border-[var(--border)]/50 pt-1.5" data-testid={`mod-union-${u._i}`}>
              <p className="text-[10px] font-bold text-[var(--text-muted)] col-span-2 sm:col-span-1">U{u._i + 1} · {u.componente_a}↔{u.componente_b}</p>
              {esMuroU ? (
                <p className="text-[10px] font-bold text-amber-600">Herraje fijo-cancel vidrio-muro</p>
              ) : (<>
              <div><label className="text-[10px] text-[var(--text-muted)]">Ángulo</label>
                <select className="w-full h-9 text-xs rounded-md border border-[var(--border)] bg-[var(--card)] text-[var(--text)] px-1" value={String(u.angulo || '90')}
                  onChange={(e) => setUnion(u._i, { angulo: e.target.value })} data-testid={`mod-union-angulo-${u._i}`}>
                  {['90', '135', '180'].map((a) => <option key={a} value={a}>{a}°</option>)}
                </select></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Holgura (mm)</label>
                <Input type="number" min="0" className="h-9 text-xs" value={u.holgura_utilizada ?? ''} placeholder={`estándar ${u.holgura_estandar ?? 5}`}
                  onChange={(e) => setUnion(u._i, { holgura_utilizada: e.target.value === '' ? '' : Math.max(0, Number(e.target.value)) })}
                  data-testid={`mod-union-holgura-${u._i}`} /></div>
              </>)}
            </div>
            );
          })}
          {modSel.tipo === 'PABM' && (r.embisagrado || 'muro') === 'muro' && (
            <p className="text-[10px] text-[var(--text-muted)]">Puerta embisagrada a MURO: bisagras muro-vidrio por regla de altura — sin bisagra vidrio-vidrio con el fijo adyacente. Las uniones puerta↔fijo aparecen al elegir "Embisagrada a fijo" y Calcular.</p>
          )}
        </div>
      )}
      {/* panel de la UNIÓN seleccionada — sugerido por receta Fase 4 + detección Fase 6.1 */}
      {unionSel && (() => {
        const esMuro = unionSel.componente_a === 'muro' || unionSel.componente_b === 'muro';
        const esPF = !esMuro && (unionSel.componente_a === 'puerta' || unionSel.componente_b === 'puerta');
        const tipoTxt = esMuro ? 'FIJO→MURO' : esPF ? 'PUERTA→FIJO' : 'FIJO→FIJO';
        const cantPend = (unionSel.cantidad === '' || unionSel.cantidad == null) && !unionSel.cantidad_sugerida;
        return (
        <div className="border border-amber-500/50 rounded-md p-2 space-y-1.5 bg-amber-500/5" data-testid="mod-union-panel">
          <p className="text-sm font-black text-amber-600">
            ⚙️ Configurar unión <span className="text-[10px] uppercase">U{unionSelI + 1} · {unionSel.componente_a}↔{unionSel.componente_b} — {tipoTxt}{esMuro ? '' : ` · ${String(unionSel.angulo || '90')}°`}</span>
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 items-end">
            {!esMuro && (
            <div><label className="text-[10px] text-[var(--text-muted)]">Ángulo</label>
              <div className="flex rounded-md border border-[var(--border)] overflow-hidden h-9" data-testid="mod-union-sel-angulo">
                {['90', '135', '180'].map((a) => (
                  <button key={a} type="button" onClick={() => setUnion(unionSelI, { angulo: a })} data-testid={`mod-union-angulo-btn-${a}`}
                    className={`flex-1 text-[11px] font-bold ${String(unionSel.angulo || '90') === a ? 'bg-amber-500 text-white' : 'text-[var(--text-muted)] bg-[var(--card)]'}`}>{a}°</button>
                ))}
              </div></div>
            )}
            <div><label className="text-[10px] text-[var(--text-muted)]">Cantidad</label>
              <Input type="number" min="0" className="h-9 text-xs" value={unionSel.cantidad ?? ''}
                placeholder={unionSel.cantidad_sugerida ? `sug. ${unionSel.cantidad_sugerida}` : 'pendiente'}
                onChange={(e) => setUnion(unionSelI, { cantidad: e.target.value })} data-testid="mod-union-sel-cant" /></div>
            <div><label className="text-[10px] text-[var(--text-muted)]">Acabado</label>
              <select className="w-full h-9 text-xs rounded-md border border-[var(--border)] bg-[var(--card)] text-[var(--text)] px-1"
                value={unionSel.acabado || ''} onChange={(e) => setUnion(unionSelI, { acabado: e.target.value })} data-testid="mod-union-sel-acabado">
                <option value="">Acabado (del elemento)</option>
                {acabados.map((a) => <option key={a} value={a}>{a}</option>)}
              </select></div>
            {!esMuro && (
            <div><label className="text-[10px] text-[var(--text-muted)]">Holgura (mm)<AyudaTip texto="Espacio que se deja entre piezas para instalación y movimiento. Estándar 5 mm por unión, editable solo para este presupuesto." /></label>
              <Input type="number" min="0" className="h-9 text-xs" value={unionSel.holgura_utilizada ?? ''} placeholder={`estándar ${unionSel.holgura_estandar ?? 5}`}
                onChange={(e) => setUnion(unionSelI, { holgura_utilizada: e.target.value === '' ? '' : Math.max(0, Number(e.target.value)) })} data-testid="mod-union-sel-holgura" /></div>
            )}
          </div>
          {cantPend && <p className="text-[10px] font-bold text-red-400" data-testid="mod-union-cant-pendiente">Cantidad pendiente de configuración — no se inventa: ingrésala manualmente.</p>}
          <p className="text-[11px] font-bold text-amber-600" data-testid="mod-union-sugerido">
            Herraje: {unionSel.item_codigo ? `${unionSel.item_codigo} (Catálogo Soluarq)`
              : unionSel.tipo_herraje === 'otro' && unionSel.otro?.nombre ? `${unionSel.otro.nombre} (otro herraje — costo pendiente)`
              : sugeridoUnion(unionSel)}
            {(unionSel.item_codigo || (unionSel.tipo_herraje === 'otro' && unionSel.otro?.nombre)) && (
              <button type="button" className="ml-1.5 text-red-400 underline" onClick={() => setUnion(unionSelI, { item_codigo: '', tipo_herraje: 'auto', otro: null })} data-testid="mod-union-quitar-item">quitar</button>
            )}
          </p>
          <div className="flex gap-1.5 flex-wrap">
            <Button type="button" size="sm" className="h-8 text-[11px] bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => usarSugerido(unionSel)} data-testid="mod-union-usar-sugerido">Usar sugerido</Button>
            <Button type="button" variant="outline" size="sm" className="h-8 text-[11px]"
              onClick={() => setPicker({ open: true, ancla: { tipo: 'union-item', i: unionSelI, union: unionSel }, sugerencia: sugeridoUnion(unionSel) })}
              data-testid="mod-union-elegir-catalogo">Elegir herraje (Catálogo Soluarq)</Button>
            <Button type="button" variant="outline" size="sm" className="h-8 text-[11px]"
              onClick={() => abrirPicker({ tipo: 'union', i: unionSelI, union: unionSel })} data-testid="mod-union-cambiar">Cambiar (Catálogo Soluarq)</Button>
          </div>
        </div>
        );
      })()}
      {/* panel del HERRAJE seleccionado */}
      {herrSel && (() => {
        const ui = herrSel.solo_visual ? unionIdxDe(herrSel) : -1;
        const uLink = ui >= 0 ? (r.uniones || [])[ui] : null;
        const cant = uLink
          ? ((uLink.cantidad === '' || uLink.cantidad == null) ? (uLink.cantidad_sugerida ?? '') : uLink.cantidad)
          : herrSel.cantidad;
        return (
          <HerrajePanel h={herrSel} cantidad={cant} acabados={acabados}
            onVerUnion={ui >= 0 ? () => setSel(`union_${ui}`) : null}
            onPatch={(patch) => patchHerraje(herrSel.hid, patch)}
            onCantidad={(v) => uLink ? setUnion(ui, { cantidad: v }) : patchHerraje(herrSel.hid, { cantidad: v })}
            onMover={(x, y) => moverHerraje(herrSel.hid, x, y)}
            onEliminar={() => eliminarHerraje(herrSel)} />
        );
      })()}
      {picker?.open && (
        <HerrajeCatalogo open onClose={() => setPicker(null)} anclaLabel={anclaLabel} sugerencia={picker.sugerencia}
          onSelect={(item) => {
            if (picker.ancla?.tipo === 'union-item') {
              if (item.codigo) {
                setUnion(picker.ancla.i, { item_codigo: item.codigo, tipo_herraje: 'auto', otro: null, acabado: item.acabado || picker.ancla.union?.acabado || '' });
                toast.success(`Herraje "${item.nombre}" asignado a la unión — genera UNA sola línea de despiece (sin duplicar)`, { duration: 6000 });
              } else {
                setUnion(picker.ancla.i, { item_codigo: '', tipo_herraje: 'otro', otro: { nombre: item.nombre, unidad: 'unidad', costo: Number(item.costo || 0) } });
                toast.warning(`"${item.nombre}" asignado como OTRO HERRAJE — ⚠️ COSTO PENDIENTE hasta asignar precio`, { duration: 7000 });
              }
              setPicker(null);
              if (item.codigo && !Number(item.costo || 0)) toast.warning('⚠️ COSTO PENDIENTE: el herraje elegido no tiene precio — al Calcular receta podrás asignarlo', { duration: 7000 });
            } else insertarCon(item, picker.ancla);
          }} />
      )}
      <p className="text-[10px] text-[var(--text-muted)]">Toca un módulo, una unión (rombo "U") o un herraje para editarlo. "+ Insertar herraje" usa el Catálogo Soluarq y el símbolo se puede arrastrar. Todo cambio técnico marca RECÁLCULO PENDIENTE — usa "Calcular receta" para regenerar uniones y despiece.</p>
      </>)}
    </div>
  );
};
